import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, AlertCircle, CircleCheck, EyeOff, Undo2, Loader2, ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';

// ── THE NAME LIVES HERE ──────────────────────────────────────────────────────
// Rob picked "Perfection Box" and reserved the right to change his mind. The route
// and the tab id are deliberately neutral (`health`), so renaming this one string
// renames the feature everywhere without breaking a saved link.
export const PERFECTION_LABEL = 'Perfection Box';

const API = process.env.REACT_APP_BACKEND_URL;

const Section = ({ tone, icon: Icon, title, blurb, children }) => {
  const tones = {
    red: 'border-rose-500/60 border-l-8 border-l-rose-600 bg-rose-500/5',
    amber: 'border-amber-500/50 border-l-8 border-l-amber-500 bg-amber-500/5',
    plain: 'border-border',
  };
  const text = { red: 'text-rose-700', amber: 'text-amber-700', plain: 'text-emerald-700' };
  return (
    <div className={`rounded-lg border-2 ${tones[tone]} p-4 mb-4`}>
      <p className={`font-medium flex items-center gap-2 ${text[tone]}`}>
        <Icon className="w-5 h-5 flex-shrink-0" /> {title}
      </p>
      {blurb && <p className="text-xs text-muted-foreground mt-0.5 ml-7">{blurb}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
};

const PerfectionBox = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/perfection`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not load the checks');
      setData(await res.json());
    } catch (e) {
      toast.error(e.message || 'Could not load the checks');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setIgnored = async (key, ignore) => {
    setBusy(key);
    try {
      const res = await fetch(
        `${API}/api/perfection/ignore${ignore ? '' : `?key=${encodeURIComponent(key)}`}`,
        {
          method: ignore ? 'POST' : 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: ignore ? JSON.stringify({ key }) : undefined,
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'That didn’t work');
      }
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  // A button that FIXES the thing here (Reports step 1, 09/10/2026 — *Recount from
  // bookings*). The server says what it is called and where it posts; this only runs it.
  const runAction = async (item) => {
    setBusy(item.key);
    try {
      const res = await fetch(`${API}/api${item.action.path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'That didn’t work');
      // `message` — a button that says in words what it did (A3's *Count in hours* /
      // *Keep all on days*); the recount's own before-and-after otherwise.
      toast.success(body.message ? body.message : body.name
        ? `${body.name}: ${body.before.used} → ${body.after.used} days used, ${body.after.remaining} left`
        : 'Done');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const Row = ({ item, ignored }) => (
    <div className="flex items-start gap-3 py-2.5 border-t border-border first:border-t-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground">{item.title}</p>
        {item.detail && (
          <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{item.detail}</p>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {item.action && !ignored && (
          <Button
            variant="outline" size="sm"
            disabled={busy === item.key}
            onClick={() => runAction(item)}
            data-testid={`perfection-action-${item.check_id}`}
          >
            {busy === item.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : item.action.label}
          </Button>
        )}
        {item.fix_route && !ignored && (
          <Button variant="ghost" size="sm" onClick={() => navigate(item.fix_route)}>
            {item.fix_label} <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        )}
        {/* No ignore button on red — and the API refuses it too, so this isn't the
            only thing holding the line. Leave going somewhere nobody chose is a
            defect, not a preference. */}
        {item.can_ignore && (
          <Button
            variant="ghost" size="sm"
            className="text-muted-foreground"
            disabled={busy === item.key}
            onClick={() => setIgnored(item.key, !ignored)}
            title={ignored ? 'Show this again' : 'Stop showing this'}
          >
            {busy === item.key
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : ignored ? <><Undo2 className="w-3.5 h-3.5 mr-1" /> Restore</>
                : <EyeOff className="w-3.5 h-3.5" />}
          </Button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking your setup…
      </div>
    );
  }
  if (!data) return null;

  const { red = [], amber = [], ignored = [], passed = [], score } = data;
  const pct = score && score.total ? Math.round((score.sorted / score.total) * 100) : 0;
  const spotless = !red.length && !amber.length;

  return (
    <div className="max-w-3xl">
      {/* PROGRESS, not a scolding. A new company would otherwise open this to a wall of
          red on day one, which teaches people to close it. A score turns the same
          information into something you can finish. */}
      <div className="rounded-lg border border-border p-4 mb-5">
        <div className="flex items-end justify-between mb-2">
          <div>
            <p className="font-heading font-semibold">
              {score.sorted} of {score.total} sorted
            </p>
            <p className="text-xs text-muted-foreground">
              {spotless
                ? 'Nothing needs your attention. Nicely done.'
                : `${red.length ? `${red.length} need${red.length === 1 ? 's' : ''} fixing` : 'Nothing broken'}${amber.length ? `, ${amber.length} worth a look` : ''}.`}
            </p>
          </div>
          <span className="text-2xl font-heading font-bold text-primary">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${red.length ? 'bg-rose-500' : amber.length ? 'bg-amber-500' : 'bg-emerald-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {red.length > 0 && (
        <Section
          tone="red" icon={AlertTriangle}
          title={`${red.length} thing${red.length === 1 ? '' : 's'} to fix`}
          blurb="Something about leave is wrong right now — where it goes, or how much people have. These can't be ignored."
        >
          {red.map((i) => <Row key={i.key} item={i} />)}
        </Section>
      )}

      {amber.length > 0 && (
        <Section
          tone="amber" icon={AlertCircle}
          title={`${amber.length} thing${amber.length === 1 ? '' : 's'} worth a look`}
          blurb="Nothing is broken. Hide any that don't apply to you — you can bring them back."
        >
          {amber.map((i) => <Row key={i.key} item={i} />)}
        </Section>
      )}

      {passed.length > 0 && (
        <Section
          tone="plain" icon={CircleCheck}
          title={spotless ? 'Everything looks right' : 'Everything else looks right'}
        >
          <ul className="text-xs text-muted-foreground leading-relaxed">
            {passed.map((p) => <li key={p.id}>· {p.summary}</li>)}
          </ul>
        </Section>
      )}

      {/* Ignored things are never gone, only moved. "I was being lazy that day" —
          Rob, 31/07/2026. */}
      {ignored.length > 0 && (
        <details className="rounded-lg border border-border p-4">
          <summary className="cursor-pointer text-sm text-muted-foreground flex items-center gap-2">
            <EyeOff className="w-4 h-4" /> Ignored ({ignored.length})
          </summary>
          <div className="mt-3">
            {ignored.map((i) => <Row key={i.key} item={i} ignored />)}
          </div>
        </details>
      )}
    </div>
  );
};

export default PerfectionBox;
